import react, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import config from '@/config';
import {
  TutorChat,
  TutorChatEvents,
  TutorChatMessage,
  TutorChatSender,
  TutorChatSendEvents,
  TutorChatL1,
  AzureTTS,
  AzureTTSInputFormat,
  AudioQueueManagerEvents,
  TutorChatLevel,
  TutorChatStyle,
  TutorChatMessageInputType,
} from '@mavatar/melingo-voice-and-avatar-sdk';
import { AvatarType } from '@mavatar/3d-avatar';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Chat.module.scss';
import classes from 'classnames';
import MessageItem, {
  TopicsGroup,
  TopicProps,
  MessageItemProps,
  getSsmlFormat,
} from '../MessageItem/MessageItem';
import MessageInput from '../MessageInput/MessageInput';
import Banner from '../Banner/Banner';
import Avatar from '../Avatar/Avatar';
import Camera from '@/components/Camera/Camera';
import VolumeController from '../VolumeController/VolumeController';
import GoBackBtn from '@/components/GoBackBtn/GoBackBtn';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { TutorType } from '@/types/user';
import CameraController from '@/components/ConversationWithTutor/CameraController/CameraController';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import ChatScroller, {
  ChatScrollerRef,
} from '@/components/ConversationWithTutor/ChatScroller/ChatScroller';
import UseAzureLanguageSettings from '@/hooks/UseAzureLanguageSettings';
import { RouterPromptGate } from '@/hooks/UseRouterPrompt';
import CloseConversationModal from '@/components/ConversationWithTutor/CloseConversationModal/CloseConversationModal';
import TutorTopicsUtils from '@/utils/tutorTopics';
import SVG from 'react-inlinesvg';
import downArrow from '@/assets/svg/down-arrow.svg';

const NUMBER_INTERATIONS = 7;
const TUTOR_STREAM_CLOSE_ERROR = `I am sorry, but there are connection problems. Please make sure you have a stable internet connection and let's try again later.`;

const getL1 = (nativeLanguage: string | null) => {
  if (!nativeLanguage) {
    return TutorChatL1.EN;
  }

  switch (nativeLanguage) {
    case 'pt':
      return TutorChatL1.PT;
      break;
    case 'es':
      return TutorChatL1.SP;
      break;
    case 'he':
      return TutorChatL1.HE;
      break;
    default:
      return TutorChatL1.EN;
  }
};

interface ChatProps {
  welcomeMessage?: TutorChatMessage;
  startMessages?: MessageItemProps[];
  topicsGroups?: TopicsGroup[];
  speechType: TutorType;
  voice?: string;
  avatarType?: AvatarType;
  layout?: 'activity' | 'fullPage' | 'history';
  hideTuorIcon?: boolean;
  endBtnText?: string;
  isFollowUpActivity?: boolean;
  onClose: (status: '' | 'end_conversation' | 'stream_close') => void;
  enableCamera?: boolean;
  onError?: (details: any) => void;
  showSelectedTopicMessages?: boolean;
  showOnlineConversation?: boolean;
  onMinimize?: () => void;
}

export interface ChatRef {
  setTopic: (topic: TopicProps) => void;
  stopAudio: () => void;
  resetChat: () => void;
  getSelectedTopicMessages: () => TutorChatMessage | null;
}

const Chat = forwardRef<ChatRef, ChatProps>(
  (
    {
      welcomeMessage,
      startMessages,
      topicsGroups,
      speechType,
      voice,
      avatarType,
      layout = 'fullPage',
      hideTuorIcon = false,
      endBtnText = '',
      isFollowUpActivity = false,
      onClose,
      enableCamera = true,
      onError,
      showSelectedTopicMessages = true,
      showOnlineConversation = true,
      onMinimize,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { nativeLanguage, user } = useAppSelector(authSelector);
    const chatRef = useRef<TutorChat | null>(null);
    const azureTTSRef = useRef<AzureTTS>();
    const isActiveRef = useRef<boolean>(false);
    const messagesContanerRef = useRef<ChatScrollerRef | null>(null);
    const [messages, setMessages] = useState<TutorChatMessage[]>([]);
    const [selectedTopic, setSelectedTopic] = useState<TopicProps | null>(null);
    const [activities, setActivities] = useState<string[] | undefined>();
    const [selectedTopicMessages, setSelectedTopicMessages] = useState<TutorChatMessage | null>(
      null
    );
    const [showInput, setShowInput] = useState<boolean>(false);
    const [isPlay, setIsPlay] = useState<boolean>(false);
    const [isMuted, setIsMuted] = useState<boolean>(false);
    const [isAvatarReady, setIsAvatarReady] = useState<boolean>(false);
    const [showPromptFromActivity, setShowPromptFromActivity] = useState<boolean>(false);
    const [showPrompt, setShowPrompt] = useState<boolean>(false);
    const routerPromptHandlersRef = useRef({
      handleConfirm: () => {},
      handleCancel: () => {},
    });
    const handleConfirm = useCallback(() => routerPromptHandlersRef.current.handleConfirm(), []);
    const handleCancel = useCallback(() => routerPromptHandlersRef.current.handleCancel(), []);
    const [cameraOff, setCameraOff] = useState<boolean>(true);
    const [avatarResizing, setAvatarResizing] = useState<boolean>(false);
    const cameraToggleInitRef = useRef(false);
    const [avatarLoadingError, setAvatarLoadingError] = useState<boolean>(false);

    const {
      avatarType: defaultAvatarType,
      avatarAccessories,
      ttsVoices,
    } = UseAzureLanguageSettings();

    const _avatarType = useMemo(() => {
      return avatarType || defaultAvatarType;
    }, [avatarType, defaultAvatarType]);

    const _voice = useMemo(() => {
      if (voice) {
        return voice;
      }

      return ttsVoices[TutorChatL1.EN];
    }, [voice, ttsVoices]);

    const stopAudio = useCallback(() => {
      if (azureTTSRef.current) {
        azureTTSRef.current.abort();
      }
    }, []);

    const showStreamCloseError = useCallback(() => {
      if (!isActiveRef.current) {
        return;
      }

      setShowInput(false);
      setMessages((prevMessages: TutorChatMessage[]) => {
        let newMessages: TutorChatMessage[] = prevMessages?.length ? [...prevMessages] : [];

        if (newMessages.filter(m => m.status === 'end').length > 0) {
          return newMessages;
        }

        if (azureTTSRef.current?.audioQueueManager) {
          azureTTSRef.current.audioQueueManager.muted = true;
        }

        newMessages = newMessages.filter(m => m.status !== 'writing');
        newMessages.push({
          sender: TutorChatSender.TUTOR,
          dateTime: new Date(),
          messageId: `tutor-stream-close-error-${Date.now()}`,
          text: TUTOR_STREAM_CLOSE_ERROR,
          speakText: TUTOR_STREAM_CLOSE_ERROR,
        });

        return newMessages;
      });
    }, []);

    useEffect(() => {
      isActiveRef.current = true;

      return () => {
        isActiveRef.current = false;
      };
    }, []);

    useEffect(() => {
      const azureTTS = new AzureTTS({
        autoPlay: true,
        inputFormat: AzureTTSInputFormat.TEXT,
        language: 'en-US',
        voice: _voice,
        getLipsync: true,
        audio: TutorTopicsUtils.getGlobalAudio(),
      });

      azureTTSRef.current = azureTTS;

      const onAudioStatusChange = ({ isPlaying }: { isPlaying: boolean }) => {
        setIsPlay(isPlaying);
      };

      const onMutedChange = () => {
        const muted = !!azureTTS.audioQueueManager?.muted;
        setIsMuted(muted);

        if (muted) {
          azureTTS.abort();
        }
      };
      onMutedChange();

      azureTTS.audioQueueManager?.dispatcher.addListener(
        AudioQueueManagerEvents.STATUS_CHANGE,
        onAudioStatusChange
      );
      azureTTS.audioQueueManager?.dispatcher.addListener(
        AudioQueueManagerEvents.MUTED_CHANGE,
        onMutedChange
      );

      return () => {
        azureTTS.audioQueueManager?.dispatcher.removeListener(
          AudioQueueManagerEvents.STATUS_CHANGE,
          onAudioStatusChange
        );
        azureTTS.audioQueueManager?.dispatcher.removeListener(
          AudioQueueManagerEvents.MUTED_CHANGE,
          onMutedChange
        );
        azureTTS.abort();
      };
    }, [_voice]);

    useEffect(() => {
      let isActive = true;
      setActivities(undefined);

      if (!selectedTopic) {
        return;
      }

      const getActivities = async () => {
        let activities: string[] = [];

        if (
          user &&
          selectedTopic?.extraField?.ellLevel &&
          selectedTopic?.extraField?.ellUnit &&
          selectedTopic?.extraField?.ellTopic &&
          !selectedTopic?.extraField?.ellActivity
        ) {
          activities = (
            await TutorTopicsUtils.getTopicActivities(
              user,
              Number(selectedTopic.extraField.ellLevel),
              Number(selectedTopic.extraField.ellUnit),
              Number(selectedTopic.extraField.ellTopic)
            )
          ).map(activity => activity.title);
        }

        if (!isActive) {
          return;
        }

        setActivities(activities);
      };

      getActivities();

      return () => {
        isActive = false;
      };
    }, [selectedTopic, user]);

    useEffect(() => {
      if (!selectedTopic || !activities) {
        return;
      }

      try {
        const chat = new TutorChat({
          l1: getL1(nativeLanguage),
          level: selectedTopic.level,
          num_iterations: NUMBER_INTERATIONS,
          style: selectedTopic.style,
          topic: selectedTopic.topic,
          // user_role: selectedTopic.user_role,
          system_role: selectedTopic.system_role,
          extraField: selectedTopic.extraField,
          activities: activities,
          quiz_less: false,
          use_streaming: config.useTutorStreaming,
        });

        chatRef.current = chat;

        setSelectedTopicMessages({
          sender: TutorChatSender.USER,
          dateTime: new Date(),
          text: selectedTopic.text,
        });

        const timerData: {
          [messageId: string]: {
            start?: Date;
            end?: Date;
          };
        } = {};
        const updateMessages = ({ message }: { message: TutorChatMessage }) => {
          if (!isActiveRef.current) {
            return;
          }

          console.log('📩 new message', chat.messages);

          setMessages([...chat.messages]);
          setShowInput(true);

          if (message.sender === TutorChatSender.USER) {
            if (message.messageId) {
              if (!timerData[message.messageId]) {
                timerData[message.messageId] = {};
              }
              timerData[message.messageId].start = new Date();
            }
          }
          if (
            message.sender === TutorChatSender.TUTOR &&
            message.status !== 'writing' &&
            message.responseToMessageId &&
            timerData[message.responseToMessageId]
          ) {
            timerData[message.responseToMessageId].end = new Date();
          }
          if (
            message.sender === TutorChatSender.TUTOR &&
            message.status !== 'writing' &&
            message.messageId === 'tutor-0'
          ) {
            timerData['start'].end = new Date();
          }

          if (message.status === 'end') {
            chat.stop();
          }

          // setTimeout(() => {
          //   messagesContanerRef.current?.scrollToBottom();
          // }, 100);
        };

        const onStartListening = () => {
          console.log('🚀 chat started listening');
        };

        const onStreamClose = () => {
          chat.stop();
          chatRef.current = null;
          console.log('Tutor Chat STREAM_CLOSE');

          showStreamCloseError();
        };

        const onChatError = (details: any) => {
          onError?.(details);
        };

        chat.dispatcher.addListener(TutorChatEvents.MASSAGE, updateMessages);
        chat.dispatcher.addListener(TutorChatEvents.WRITING, updateMessages);
        chat.dispatcher.addListener(TutorChatEvents.USER_MASSAGE_SENDED, updateMessages);
        chat.dispatcher.addListener(TutorChatEvents.START_LISTENING, onStartListening);
        chat.dispatcher.addListener(TutorChatEvents.STREAM_CLOSE, onStreamClose);
        chat.dispatcher.addListener(TutorChatEvents.ERROR, onChatError);

        chat.start();
        timerData['start'] = {
          start: new Date(),
        };

        return () => {
          chat.stop();
          chat.dispatcher.removeListener(TutorChatEvents.MASSAGE, updateMessages);
          chat.dispatcher.removeListener(TutorChatEvents.WRITING, updateMessages);
          chat.dispatcher.removeListener(TutorChatEvents.USER_MASSAGE_SENDED, updateMessages);
          chat.dispatcher.removeListener(TutorChatEvents.START_LISTENING, onStartListening);
          chat.dispatcher.removeListener(TutorChatEvents.STREAM_CLOSE, onStreamClose);
          chat.dispatcher.removeListener(TutorChatEvents.ERROR, onChatError);
          chatRef.current = null;

          console.log(
            'Summary of the conversation times:',
            Object.keys(timerData).reduce(
              (acc, messageId) => {
                if (
                  timerData[messageId] &&
                  timerData[messageId].start &&
                  timerData[messageId].end
                ) {
                  const seconds =
                    ((timerData[messageId].end as Date).getTime() -
                      (timerData[messageId].start as Date).getTime()) /
                    1000;
                  acc.list[messageId] = {
                    start: timerData[messageId].start as Date,
                    end: timerData[messageId].end as Date,
                    seconds,
                  };
                  acc.count += 1;
                  acc.total += seconds;
                  acc.avg = acc.total / acc.count;
                }
                return acc;
              },
              {
                list: {},
                total: 0,
                count: 0,
                avg: 0,
              } as {
                list: {
                  [messageId: string]: {
                    start: Date;
                    end: Date;
                    seconds: number;
                  };
                };
                total: number;
                count: number;
                avg: number;
              }
            )
          );
        };
      } catch (e) {
        console.log(e);
      }
    }, [selectedTopic, nativeLanguage, onError, activities, showStreamCloseError]);

    // useEffect(() => {
    //   setTimeout(() => {
    //     messagesContanerRef.current?.scrollToBottom();
    //   }, 100);
    // }, [startMessages]);

    const sendMessage = useCallback((text: string, inputType: TutorChatMessageInputType) => {
      console.log('📩 new message send  :', text);
      chatRef.current?.send(TutorChatSendEvents.MASSAGE, text, inputType);
    }, []);

    const handleTopicSelect = useCallback(
      (topic: TopicProps) => {
        if (selectedTopic?.topic !== topic.topic) {
          setSelectedTopic(topic);
        }
      },
      [selectedTopic]
    );

    const resetChat = useCallback(() => {
      setMessages([]);
      setSelectedTopic(null);
      setSelectedTopicMessages(null);
      setShowInput(false);
    }, []);

    const onHidePrivateTutor = useCallback(() => {
      if (isFollowUpActivity) {
        setShowPromptFromActivity(true);
        return;
      }

      if (layout === 'activity' && showInput && showOnlineConversation) {
        setShowPromptFromActivity(true);
        return;
      }

      if (layout === 'activity') {
        onClose?.(showInput ? '' : 'end_conversation');
        return;
      }

      navigate(-1);
    }, [isFollowUpActivity, layout, showInput, showOnlineConversation, onClose, navigate]);

    const closeConversationHandleConfirm = useCallback(() => {
      if (showPromptFromActivity) {
        setShowPromptFromActivity(false);
        onClose?.('end_conversation');
        return;
      }

      handleConfirm();
    }, [showPromptFromActivity, handleConfirm, onClose]);

    const closeConversationHandleCancel = useCallback(() => {
      if (showPromptFromActivity) {
        setShowPromptFromActivity(false);
        return;
      }

      handleCancel();
    }, [showPromptFromActivity, handleCancel]);

    const onMessageItemReady = useCallback(
      (isWriting: boolean, element?: HTMLElement | null, hasTopics?: boolean) => {
        setTimeout(() => {
          if (hasTopics && element) {
            const prevBubble = element.previousElementSibling as HTMLElement | null;
            (prevBubble ?? element).scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            messagesContanerRef.current?.scrollToBottom();
          }
        }, 100);
      },
      []
    );

    const showSpeaker: boolean = useMemo(() => {
      return speechType !== 'TEXT';
    }, [speechType]);

    const showAvatar: boolean = useMemo(() => {
      return speechType === 'AVATAR' && !avatarLoadingError;
    }, [speechType, avatarLoadingError]);

    useEffect(() => {
      if (showSpeaker && !isMuted && !showOnlineConversation && messages?.length) {
        const message = messages[0];

        if (!message.disableAutoSpeaker && message.speakText) {
          const { lang, text, inputFormat } = getSsmlFormat(
            message.speakText,
            TutorChatL1.EN,
            ttsVoices
          );

          const azureTTS = new AzureTTS({
            autoPlay: false,
            inputFormat: inputFormat,
            language: lang,
            voice: _voice,
            getLipsync: true,
            audio: TutorTopicsUtils.getGlobalAudio(),
          });
          azureTTS.tts(text);
        }
      }
    }, [showOnlineConversation, showSpeaker, isMuted, _voice, messages, ttsVoices]);

    const volumeController = useMemo(() => {
      return (
        showSpeaker &&
        azureTTSRef.current && (
          <VolumeController
            audioQueueManager={azureTTSRef.current?.audioQueueManager}
            layout='line'
          />
        )
      );
    }, [azureTTSRef.current, showSpeaker, showAvatar]);

    const cameraController = useMemo(() => {
      return (
        enableCamera &&
        showAvatar && (
          <CameraController
            off={cameraOff}
            onClick={() => {
              setCameraOff(perf => !perf);
            }}
          />
        )
      );
    }, [enableCamera, cameraOff, showAvatar]);

    const controllers = useMemo(() => {
      return (
        <>
          {volumeController}
          {cameraController}
        </>
      );
    }, [volumeController, cameraController]);

    const header = useMemo(() => {
      if (!showAvatar) {
        return (
          <Banner
            layout={layout}
            onBackBtnClick={onHidePrivateTutor}
            tools={controllers}
            goBackBtnText={
              isFollowUpActivity
                ? t('tutorEndActivityBtn').toString()
                : t('tutorEndConversationBtn').toString()
            }
            onMinimize={onMinimize}
          />
        );
      }

      return (
        <div className={styles.header}>
          <GoBackBtn
            className={styles.backBtn}
            showIcon={true}
            text={
              isFollowUpActivity
                ? t('tutorEndActivityBtn').toString()
                : t('tutorEndConversationBtn').toString()
            }
            emitClick={onHidePrivateTutor}
          />
          <div className={styles.headerTools}>{controllers}</div>
          <div className={styles.mobileAvatarCameraBtn}>
            {cameraController}
            {onMinimize && (
              <button
                className={styles.minimizeBtn}
                onClick={onMinimize}
                aria-label='Minimize tutor'
              >
                <SVG src={downArrow} />
              </button>
            )}
          </div>
        </div>
      );
    }, [t, showAvatar, layout, onHidePrivateTutor, controllers, onMinimize, cameraController]);

    const avatarLabel = useMemo(() => {
      const label = t('my_tutor_page.avatarLabel');

      return (
        <>
          <span>{label}</span>
          <svg width='200' height='200' viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'>
            <defs>
              <path
                id='text-curve'
                d='M 40 168.208 C 73.171 201.264 126.829 201.264 160 168.208'
                fill='transparent'
              />
            </defs>
            <text>
              <textPath href='#text-curve' startOffset='0%'>
                {label}
              </textPath>
            </text>
          </svg>
        </>
      );
    }, [t]);

    const avatar = useMemo(() => {
      if (!showAvatar) {
        return <></>;
      }

      return (
        <Avatar
          type={_avatarType}
          audioQueueManager={azureTTSRef.current?.audioQueueManager || undefined}
          waitingFace='smile'
          accessories={avatarAccessories}
          onLoading={(isLoading: boolean) => {
            setIsAvatarReady(!isLoading);
          }}
          camera={[0, 1.66, 1, 0, 1.66, 0]} // [0.2, 1.66, 1, 0, 1.66, 0]
          onLoadingError={url => {
            setAvatarLoadingError(true);
          }}
        />
      );
    }, [showAvatar, azureTTSRef.current?.audioQueueManager]);

    const waitToAvatar: boolean = useMemo(() => {
      return showAvatar && !isAvatarReady;
    }, [showAvatar, isAvatarReady]);

    useEffect(() => {
      if (!cameraToggleInitRef.current) {
        cameraToggleInitRef.current = true;
        return;
      }
      if (!showAvatar) return;
      setAvatarResizing(true);
      const timer = setTimeout(() => setAvatarResizing(false), 400);
      return () => clearTimeout(timer);
    }, [cameraOff]);

    const onWheel = useCallback((e: react.WheelEvent) => {
      messagesContanerRef.current?.scrollDelta(e.deltaY);
    }, []);

    const getMessageItemGlobalProps = () => {
      return {
        azureTTS: azureTTSRef.current,
        showSpeaker: showSpeaker,
        voice: _voice,
        isMuted: isMuted,
        showTuorIcon: !showAvatar && !hideTuorIcon,
        onWritingChange: onMessageItemReady,
        userName: user?.metadata?.firstName || user?.metadata?.lastName || 'User',
      };
    };

    useImperativeHandle(ref, () => {
      return {
        setTopic: handleTopicSelect,
        stopAudio: stopAudio,
        resetChat: resetChat,
        getSelectedTopicMessages: () => {
          return selectedTopicMessages;
        },
      };
    }, [handleTopicSelect, stopAudio, resetChat, selectedTopicMessages]);

    return (
      <div
        className={classes(styles.chatContainer, {
          [styles.chatContainerWithAvatar]: showAvatar,
          [styles.chatContainerInPopup]: layout === 'activity',
          [styles.chatContainerInHistory]: layout === 'history',
        })}
      >
        <div className={styles.chat}>
          {layout !== 'history' && <div className={styles.headerParent}>{header}</div>}

          {waitToAvatar && (
            <ChatScroller className={styles.chatContent} ref={messagesContanerRef}>
              <div className={styles.centerContainer}>
                <MessageItem
                  message={{
                    sender: TutorChatSender.TUTOR,
                    status: 'writing',
                  }}
                  {...getMessageItemGlobalProps()}
                />
              </div>
            </ChatScroller>
          )}

          {!waitToAvatar && (
            <ChatScroller className={styles.chatContent} ref={messagesContanerRef}>
              <div className={styles.centerContainer}>
                {startMessages &&
                  startMessages.map((message, messageIndex) => (
                    <MessageItem
                      key={message.message?.messageId || `start-message--${messageIndex}`}
                      {...message}
                      {...getMessageItemGlobalProps()}
                    />
                  ))}
                {welcomeMessage && (
                  <MessageItem
                    message={welcomeMessage}
                    handleTopic={handleTopicSelect}
                    topicsGroups={selectedTopicMessages ? undefined : topicsGroups}
                    {...getMessageItemGlobalProps()}
                  />
                )}
                {selectedTopicMessages && showSelectedTopicMessages && (
                  <MessageItem message={selectedTopicMessages} {...getMessageItemGlobalProps()} />
                )}
                {showOnlineConversation && selectedTopicMessages && messages.length === 0 && (
                  <MessageItem
                    message={{
                      sender: TutorChatSender.TUTOR,
                      status: 'writing',
                    }}
                    {...getMessageItemGlobalProps()}
                  />
                )}
                {showOnlineConversation &&
                  messages.map((msg, msgIndex) => {
                    let topicsGroups: TopicsGroup[] | undefined;
                    let handleTopic: ((topic: TopicProps) => void) | undefined;

                    if (endBtnText && msgIndex === messages.length - 1 && msg.status === 'end') {
                      topicsGroups = [
                        {
                          topics: [
                            {
                              level: TutorChatLevel.A1,
                              style: TutorChatStyle.SMALLTALK,
                              system_role: '',
                              user_role: '',
                              topic: endBtnText,
                              text: endBtnText,
                            },
                          ],
                        },
                      ];

                      handleTopic = (topic: TopicProps) => {
                        onClose('end_conversation');
                      };
                    }

                    if (
                      endBtnText &&
                      msgIndex === messages.length - 1 &&
                      msg.text === TUTOR_STREAM_CLOSE_ERROR &&
                      msg.sender === TutorChatSender.TUTOR
                    ) {
                      topicsGroups = [
                        {
                          topics: [
                            {
                              level: TutorChatLevel.A1,
                              style: TutorChatStyle.SMALLTALK,
                              system_role: '',
                              user_role: '',
                              topic: endBtnText,
                              text: endBtnText,
                            },
                          ],
                        },
                      ];

                      handleTopic = (topic: TopicProps) => {
                        onClose('stream_close');
                      };
                    }

                    return (
                      <MessageItem
                        key={msg.messageId || `msg--${msgIndex}`}
                        message={msg}
                        handleTopic={handleTopic}
                        topicsGroups={topicsGroups}
                        {...getMessageItemGlobalProps()}
                      />
                    );
                  })}
              </div>
            </ChatScroller>
          )}

          {showInput && showOnlineConversation && (
            <div className={styles.messageInputContainer}>
              <div className={styles.centerContainer}>
                <MessageInput onSend={sendMessage} isRecordingDisabled={isPlay} />
              </div>
            </div>
          )}

          {showAvatar && (
            <div className={classes(styles.avatarGroup, { [styles.cameraIsOff]: cameraOff })}>
              <div className={classes(styles.avatar, { [styles.cameraIsOff]: cameraOff })}>
                {avatarResizing && (
                  <div className={styles.avatarResizingOverlay}>
                    <AppLoaderCircle />
                  </div>
                )}
                <div
                  className={classes(styles.cameraWrapper, styles.avatarCameraWrapper)}
                  onWheel={onWheel}
                >
                  <div className={styles.camera}>{avatar}</div>
                  <div className={styles.avatarLabel}>{avatarLabel}</div>
                  {showSpeaker && azureTTSRef.current && (
                    <div className={styles.mobileAvatarMuteBtn}>
                      <VolumeController
                        audioQueueManager={azureTTSRef.current?.audioQueueManager}
                        layout='button'
                      />
                    </div>
                  )}
                </div>
                {enableCamera && (
                  <div
                    className={classes(styles.cameraWrapper, styles.userCameraWrapper)}
                    onWheel={onWheel}
                  >
                    <div className={styles.camera}>
                      <Camera
                        aspectRatio={1}
                        permissionClassName={styles.cameraPermission}
                        isOn={!cameraOff}
                        toggleCamera={() => {
                          setCameraOff(perf => !perf);
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {layout === 'fullPage' && (
          <RouterPromptGate
            when={showInput && showOnlineConversation}
            onShowPromptChange={setShowPrompt}
            handlersRef={routerPromptHandlersRef}
          />
        )}

        <CloseConversationModal
          show={showPrompt || showPromptFromActivity}
          handleConfirm={closeConversationHandleConfirm}
          handleCancel={closeConversationHandleCancel}
          title={isFollowUpActivity ? t('tutorRouterPrompt.activity.title') : undefined}
          description={isFollowUpActivity ? t('tutorRouterPrompt.activity.description') : undefined}
          cancelBtnText={
            isFollowUpActivity ? (t('tutorRouterPrompt.activity.cancelBtn') as string) : undefined
          }
          confirmBtnText={
            isFollowUpActivity ? (t('tutorRouterPrompt.activity.confirmBtn') as string) : undefined
          }
        />
      </div>
    );
  }
);

export default Chat;
